import {
	Delete,
	Edit,
	KeyboardArrowDown,
	KeyboardArrowUp,
	Warning,
} from '@mui/icons-material'
import {
	Box,
	Button,
	Fade,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Theme,
	Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import { percent } from 'csx'
import { observer } from 'mobx-react'
import { useRouteMatch } from 'react-router'
import {
	ConfigurableFormPackageProcessor,
	FormPackageProcessorMetadata,
} from '../../../../../../../../api/DTOtemp'
import IconButtonLink from '../../../../../../../../components/links/IconButtonLink'
import { usePrompts } from '../../../../../../../../services/notifications/PromptService'
import { makeChildRoute } from '../../../../../../../../utils/CreateChildRoute'
import { DeleteFormPackageProcessorDialog } from './DeleteFormPackageProcessorDialog'

type FormPackageProcessorsTableProps = {
	processorInstances: FormPackageProcessorMetadata[]
	processors: ConfigurableFormPackageProcessor[]

	processorOrderDirty: boolean

	onMoveProcessors: (processors: FormPackageProcessorMetadata[]) => void
	onDeleteProcessor: (processorId: number) => void
	onSaveProcessorPositions: (processors: FormPackageProcessorMetadata[]) => void
}

export const FormPackageProcessorsTable = observer(
	(props: FormPackageProcessorsTableProps) => {
		const styles = useStyles()

		const { url } = useRouteMatch()

		const promptService = usePrompts()

		const onProcessorMoved = (
			processor: FormPackageProcessorMetadata,
			direction: 'earlier' | 'later',
		) => {
			const processorInstances = props.processorInstances
			const targetIndex = processorInstances.indexOf(processor)

			if (targetIndex === -1)
				throw Error(
					'onProcessorMoved was passed a form which does not exist in processor collection',
				)

			if (targetIndex === 0 && direction === 'earlier') return

			if (
				targetIndex === processorInstances.length - 1 &&
				direction === 'later'
			)
				return

			console.log(
				'handling position change request with direction: ',
				direction,
			)

			const nextIndex =
				direction === 'earlier' ? targetIndex - 1 : targetIndex + 1

			const temp = processorInstances[nextIndex]
			temp.index = targetIndex
			processorInstances[targetIndex].index = nextIndex

			processorInstances[nextIndex] = processorInstances[targetIndex]
			processorInstances[targetIndex] = temp

			props.onMoveProcessors(processorInstances)
		}

		return (
			<>
				<Fade in>
					<TableContainer className={styles.tableContentRoot} component={Paper}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Position</TableCell>
									<TableCell>Name</TableCell>
									<TableCell>Type</TableCell>
									<TableCell>Description</TableCell>
									<TableCell align="right">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{props.processorInstances.map((processor, i) => {
									const processorType = props.processors.find(
										(v) => v.processorId === processor.processorId,
									)
									return (
										<TableRow key={processor.id}>
											<TableCell>{processor.index}</TableCell>
											<TableCell>
												{' '}
												<Typography
													variant="inherit"
													color={processorType === undefined ? 'error' : ''}
												>
													{processor.name}
												</Typography>
											</TableCell>
											<TableCell>
												{processorType?.displayName ?? (
													<Box display="flex" alignItems="center" gap={1}>
														<Warning color="error" fontSize="small" />
														<Typography variant="subtitle2" color="error">
															Plugin Missing
														</Typography>
													</Box>
												)}
											</TableCell>
											<TableCell>{processor.description}</TableCell>
											<TableCell align="right">
												{processorType !== undefined && (
													<IconButtonLink
														to={makeChildRoute(
															`_processors/${processor.id}`,
															url,
														)}
													>
														<Edit />
													</IconButtonLink>
												)}

												<IconButton
													onClick={() => {
														promptService
															.showDialog((props) => (
																<DeleteFormPackageProcessorDialog
																	processorName={processor.name}
																	onCancel={() => props.close('cancel')}
																	onConfirm={() => props.close('okay')}
																/>
															))
															.then((v) => {
																if (v === 'okay')
																	props.onDeleteProcessor(processor.id)
															})
													}}
													size="large"
												>
													<Delete />
												</IconButton>
												<>
													<IconButton
														disabled={i === 0}
														onClick={() =>
															onProcessorMoved(processor, 'earlier')
														}
														size="large"
													>
														<KeyboardArrowUp />
													</IconButton>
													<IconButton
														disabled={i === props.processorInstances.length - 1}
														onClick={() => onProcessorMoved(processor, 'later')}
														size="large"
													>
														<KeyboardArrowDown />
													</IconButton>
												</>
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</Fade>

				<div className={clsx(styles.fullWidth, styles.lowerFlexBox)}>
					<Button
						color="primary"
						variant="contained"
						onClick={() =>
							props.onSaveProcessorPositions(props.processorInstances)
						}
						disabled={!props.processorOrderDirty}
						className={styles.noTextTransform}
					>
						Save New Positions
					</Button>
				</div>
			</>
		)
	},
)

const useStyles = makeStyles((theme: Theme) => ({
	fullWidth: {
		width: percent(100),
	},

	lowerFlexBox: {
		display: 'flex',
		flexDirection: 'row-reverse',

		padding: theme.spacing(2),
	},

	noTextTransform: {
		textTransform: 'none',
	},

	tableContentRoot: {
		overflow: 'auto',
	},
}))
